import { collection, query, orderBy, getDocs, addDoc, where, deleteDoc, doc, getDoc, updateDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { db } from "lib/firebase";
import { useCallback, useEffect, useState } from "react";
import { Template } from "../types/template";
import { useAuth } from "contexts/AuthContext";

export const useTemplates = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const templatesRef = collection(db, 'templates');
      let q = query(templatesRef, orderBy('createdAt', 'desc'));

      if (user?.role === "super_admin") {
        q = query(templatesRef, orderBy('createdAt', 'desc'));
      } else {
        q = query(templatesRef, where('organizationId', '==', user?.organizationId), orderBy('createdAt', 'desc'));
      }

      const snapshot = await getDocs(q);

      const loadedTemplates = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          description: data.description,
          type: data.type,
          fileUrl: data.fileUrl,
          fileName: data.fileName,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          organizationId: data.organizationId
        } as Template;
      });

      setTemplates(loadedTemplates);
      setError(null);
    } catch (err) {
      console.error("Hiba a sablonok betöltése során:", err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const addTemplate = useCallback(async (template: Template) => {
    try {
      const templatesRef = collection(db, 'templates');
      await addDoc(templatesRef, template);
    } catch (err) {
      console.error("Hiba a sablon hozzáadása során:", err);
      throw err;
    }
  }, []);

  const getTemplateByType = useCallback(async (type: string) => {
    try {
      const templatesRef = collection(db, 'templates');
      const q = query(templatesRef, where('type', '==', type), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      } as Template));
    } catch (err) {
      console.error("Hiba a sablon típus szerinti lekérdezése során:", err);
      throw err;
    }
  }, []);

  const deleteTemplate = useCallback(async (id: string) => {
    try {
      const templatesRef = collection(db, 'templates');
      const docRef = doc(templatesRef, id);
      await deleteDoc(docRef);
    } catch (err) {
      console.error("Hiba a sablon törlése során:", err);
      throw err;
    }
  }, []);

  const deleteTemplatesBulk = useCallback(async (ids: string[]) => {
    try {
      // Batch művelet létrehozása Firebase v9 szerint
      const batch = writeBatch(db);
      
      // Minden id-hez hozzáadunk egy törlési műveletet
      for (const id of ids) {
        const docRef = doc(db, 'templates', id);
        batch.delete(docRef);
      }
      
      // Végrehajtjuk az összes törlést egy tranzakcióban
      await batch.commit();
      return true;
    } catch (err) {
      console.error("Hiba a sablonok tömeges törlése során:", err);
      throw err;
    }
  }, []);

  const updateTemplate = useCallback(async (id: string, templateData: Partial<Template>) => {
    try {
      const templateRef = doc(db, 'templates', id);
      
      // Frissítési idő hozzáadása
      const updateData = {
        ...templateData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(templateRef, updateData);
      return true;
    } catch (err) {
      console.error("Hiba a sablon frissítése során:", err);
      throw err;
    }
  }, []);

  return { templates, loading, error, refreshTemplates: loadTemplates, addTemplate, getTemplateByType, deleteTemplate, deleteTemplatesBulk, updateTemplate };
};